
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { SocialAuth } from './SocialAuth';
import { Mail, ArrowRight, Loader2, ArrowLeft, KeyRound } from 'lucide-react';
import { useEmailAuth } from '../../hooks/use-email-auth';
import { maskEmail } from '../../lib/utils/util';

interface SignInProps {
    onLogin: (provider: string, email?: string) => Promise<void>;
}

export const SignIn: React.FC<SignInProps> = ({ onLogin }) => {
    const [isSocialLoading, setIsSocialLoading] = useState(false);

    const {
        step,
        isLoading,
        error,
        cooldown,
        handleSendCode,
        handleVerifyCode,
        handleResend,
        reset,
        email
    } = useEmailAuth((verifiedEmail) => onLogin('EMAIL', verifiedEmail));

    const handleSocial = async (provider: string) => {
        setIsSocialLoading(true);
        try {
            await onLogin(provider);
        } catch (e) {
            console.error(e);
        } finally {
            setIsSocialLoading(false);
        }
    };

    const onSubmitEmail = (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        handleSendCode(formData.get('email') as string);
    };

    const onSubmitOtp = (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        handleVerifyCode(formData.get('otp') as string);
    };

    if (step === 'OTP') {
        return (
            <div className="space-y-6 animate-fade-in">
                <div className="text-center">
                    <button 
                        onClick={reset} 
                        className="absolute left-6 top-6 p-2 rounded-full hover:bg-stone-100 transition-colors text-stone-400"
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <div className="w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4 text-stone-600">
                        <KeyRound size={20} />
                    </div>
                    <h3 className="font-serif text-2xl font-bold text-stone-900">Check your inbox</h3>
                    <p className="text-sm text-stone-500 mt-1">
                        We sent a code to <span className="font-bold text-stone-800">{maskEmail(email)}</span>
                    </p>
                </div>

                <form onSubmit={onSubmitOtp} className="space-y-4">
                    <input 
                        type="text" 
                        name="otp"
                        placeholder="000000"
                        className="w-full text-center text-3xl font-mono tracking-[0.5em] py-4 bg-stone-50 border border-stone-200 rounded-md focus:ring-2 focus:ring-nobel-gold outline-none transition-all placeholder:text-stone-300"
                        maxLength={6}
                        autoFocus
                        required
                    />
                    
                    {error && (
                        <p className="text-xs text-red-500 text-center font-bold">{error}</p>
                    )}

                    <button 
                        type="submit" 
                        disabled={isLoading}
                        className="w-full py-3 bg-stone-900 text-white rounded-md font-bold text-xs hover:bg-nobel-gold hover:text-stone-900 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {isLoading ? <Loader2 className="animate-spin" size={16} /> : "Verify & Login"}
                    </button>
                </form>

                <div className="text-center">
                    <button 
                        onClick={handleResend}
                        disabled={cooldown > 0}
                        className="text-xs font-bold text-stone-500 hover:text-stone-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {cooldown > 0 ? `Resend code in ${cooldown}s` : "Didn't receive code? Resend"}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="text-center">
                <h3 className="font-serif text-2xl font-bold text-stone-900">Welcome Back</h3>
                <p className="text-sm text-stone-500 mt-1">Sign in to access your wallet</p>
            </div>

            <SocialAuth onConnect={handleSocial} isLoading={isSocialLoading} />

            <div className="flex items-center gap-2">
                <div className="h-px bg-stone-200 flex-1"></div>
                <span className="text-[10px] text-stone-400 uppercase font-bold tracking-wider">Or</span>
                <div className="h-px bg-stone-200 flex-1"></div>
            </div>

            <form onSubmit={onSubmitEmail} className="space-y-3">
                <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                    <input 
                        type="email" 
                        name="email"
                        placeholder="name@example.com"
                        className="w-full pl-10 pr-4 py-3 bg-stone-50 border border-stone-200 rounded-md focus:ring-2 focus:ring-nobel-gold outline-none text-sm transition-all"
                        required
                    />
                </div>
                {error && <p className="text-xs text-red-500 ml-1">{error}</p>}
                
                <button 
                    type="submit" 
                    disabled={isLoading}
                    className="w-full py-3 bg-stone-900 text-white rounded-md font-bold text-xs hover:bg-nobel-gold hover:text-stone-900 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 shadow-md"
                >
                    {isLoading ? <Loader2 className="animate-spin" size={16} /> : "Continue with Email"}
                    {!isLoading && <ArrowRight size={14} />}
                </button>
            </form>
        </div>
    );
};
